import styled from '@emotion/styled';
import { SocketMapObservationStats } from 'src_common/common/mobx-utils/Socket/SocketMapObservationStats';

export const BAR_HEIGHT_PX = 36;

export const BarRoot = styled('div', { label: 'BarRoot' })`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 100000;
    min-height: ${BAR_HEIGHT_PX}px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 4px 12px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
    font-size: 11px;
    line-height: 1.2;
    color: #e8e8e8;
    background: rgba(18, 18, 22, 0.94);
    border-bottom: 1px solid #444;
    overflow-x: auto;
    white-space: nowrap;
`;

export const BarTitle = styled('span', { label: 'BarTitle' })`
    font-weight: 700;
    color: #9cdcfe;
    flex-shrink: 0;
`;

export const StatChip = styled('span', { label: 'StatChip' })<{ level?: 'ok' | 'warn' | 'alert' }>`
    flex-shrink: 0;
    padding: 2px 6px;
    border-radius: 4px;
    background: ${({ level }): string => {
        if (level === 'alert') {
            return '#5c1a1a';
        }
        if (level === 'warn') {
            return '#4a3f14';
        }
        return '#2a2a32';
    }};
    border: 1px solid
        ${({ level }): string => {
            if (level === 'alert') {
                return '#f44747';
            }
            if (level === 'warn') {
                return '#dcdcaa';
            }
            return '#555';
        }};
`;

export const BarButton = styled('button', { label: 'BarButton' })`
    flex-shrink: 0;
    padding: 2px 8px;
    font: inherit;
    font-size: 10px;
    color: #ccc;
    background: #333;
    border: 1px solid #666;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
        background: #444;
    }
`;

export const BarActions = styled('div', { label: 'BarActions' })`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-left: auto;
`;

export const InfoButton = styled('button', { label: 'InfoButton' })`
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    padding: 0;
    font: inherit;
    font-size: 13px;
    font-weight: 700;
    line-height: 1;
    color: #9cdcfe;
    background: #2a2a32;
    border: 1px solid #555;
    border-radius: 50%;
    cursor: pointer;

    &:hover {
        background: #3a3a44;
        border-color: #9cdcfe;
    }
`;

export const LegendBackdrop = styled('button', { label: 'LegendBackdrop' })`
    position: fixed;
    inset: 0;
    z-index: 100001;
    margin: 0;
    padding: 0;
    border: none;
    background: rgba(0, 0, 0, 0.35);
    cursor: default;
`;

export const LegendPanel = styled('div', { label: 'LegendPanel' })`
    position: fixed;
    top: ${BAR_HEIGHT_PX + 8}px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 100002;
    box-sizing: border-box;
    width: min(560px, calc(100vw - 24px));
    max-height: min(70vh, 520px);
    overflow: auto;
    padding: 16px 20px 20px;
    font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
    font-size: 13px;
    line-height: 1.45;
    color: #e8e8e8;
    background: #1e1e24;
    border: 1px solid #555;
    border-radius: 8px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
    white-space: normal;
`;

export const LegendCloseButton = styled('button', { label: 'LegendCloseButton' })`
    position: absolute;
    top: 8px;
    right: 10px;
    width: 28px;
    height: 28px;
    padding: 0;
    font-size: 20px;
    line-height: 1;
    color: #aaa;
    background: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
        color: #fff;
        background: #333;
    }
`;

export const LegendSectionTitle = styled('h2', { label: 'LegendSectionTitle' })`
    margin: 0 0 12px;
    padding-right: 32px;
    font-size: 15px;
    font-weight: 700;
    color: #9cdcfe;
`;

export const LegendSubTitle = styled('h3', { label: 'LegendSubTitle' })`
    margin: 0 0 8px;
    font-size: 13px;
    font-weight: 700;
    color: #9cdcfe;
`;

export const LegendSection = styled('section', { label: 'LegendSection' })`
    margin-bottom: 16px;

    &:last-of-type {
        margin-bottom: 0;
    }
`;

export const LegendText = styled('p', { label: 'LegendText' })`
    margin: 0 0 8px;
    color: #ccc;

    &:last-child {
        margin-bottom: 0;
    }

    code {
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        font-size: 11px;
        padding: 1px 4px;
        background: #2a2a32;
        border-radius: 3px;
    }
`;

export const LegendEntry = styled('div', { label: 'LegendEntry' })`
    margin-bottom: 8px;
    color: #ccc;

    strong {
        color: #dcdcaa;
    }

    code {
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        font-size: 11px;
        padding: 1px 4px;
        background: #2a2a32;
        border-radius: 3px;
    }

    &::before {
        content: attr(data-term);
        display: block;
        font-weight: 600;
        color: #ce9178;
        margin-bottom: 2px;
        font-size: 12px;
    }
`;

export const formatMapStat = (label: string, stats: SocketMapObservationStats, wire: boolean): string => {
    const suffix = wire ? '' : ' (cache)';
    return `${label} ${stats.active}/${stats.total}${suffix}`;
};

export const statLevel = (active: number, warnAbove: number, alertAbove: number): 'ok' | 'warn' | 'alert' => {
    if (active >= alertAbove) {
        return 'alert';
    }
    if (active >= warnAbove) {
        return 'warn';
    }
    return 'ok';
};
